import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SessionRefreshService } from '../services/session-refresh.service';

/**
 * Auth Interceptor
 * Automatically includes credentials (HTTP-only cookies) in all requests
 * Implements retry mechanism: on 401, refreshes the session once and retries
 * before redirecting to SSO. Refresh de-duplication lives in SessionRefreshService,
 * shared with ApiHandlerService.fetchData.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private sessionRefresh: SessionRefreshService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Clone request and add withCredentials to include cookies
    const clonedReq = req.clone({
      withCredentials: true,
    });

    return next.handle(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // 403 means the user lacks permission, not that the session expired;
        // redirecting would bounce straight back from SSO. Let callers handle it.
        if (error.status === 401) {
          return this.handle401Error(clonedReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Refresh the session, then retry the original request once.
   * A second 401 on the retry means the refreshed session is still rejected.
   */
  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return from(this.sessionRefresh.refresh()).pipe(
      catchError((refreshError) => {
        this.sessionRefresh.redirectToSignin();
        return throwError(() => refreshError);
      }),
      switchMap(() =>
        next.handle(request).pipe(
          catchError((retryError: HttpErrorResponse) => {
            if (retryError.status === 401) {
              this.sessionRefresh.redirectToSignin();
            }
            return throwError(() => retryError);
          })
        )
      )
    );
  }
}
